import type { Permission } from '@idc-forms/permissions'
import {
  ArrowLeft,
  Briefcase,
  Building2,
  CopyCheck,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  ShieldAlert,
  UserRound
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { SEGMENTS } from '@/app/router/routes'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { Button } from '@/shared/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/shared/ui/empty'
import { Separator } from '@/shared/ui/separator'

interface AccessibleModule {
  label: string
  icon: LucideIcon
  to: string
  permission?: Permission
}

const MODULES: AccessibleModule[] = [
  {
    label: 'Inicio',
    icon: LayoutDashboard,
    to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.dashboard}`
  },
  {
    label: 'Clientes',
    icon: UserRound,
    to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.clients}`,
    permission: 'ReadAllClients' as Permission
  },
  {
    label: 'Negociaciones',
    icon: Handshake,
    to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.negotiations}`,
    permission: 'ReadAllNegotiations' as Permission
  },
  {
    label: 'Postventa',
    icon: CopyCheck,
    to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.deliveries}`,
    permission: 'ReadAllDeliveries' as Permission
  },
  {
    label: 'Inventario',
    icon: Briefcase,
    to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.inventory}`,
    permission: 'ReadAvailability' as Permission
  },
  { label: 'Empresa', icon: Building2, to: `/${SEGMENTS.admin.root}/${SEGMENTS.admin.business}` }
]

const Unauthorized = () => {
  const navigate = useNavigate()
  const { has, isAdmin } = usePermissions()

  const accessibleModules = useMemo(() => {
    if (isAdmin) return MODULES
    return MODULES.filter((m) => !m.permission || has(m.permission))
  }, [isAdmin, has])

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <Empty className="max-w-lg border border-dashed">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ShieldAlert className="text-destructive" />
          </EmptyMedia>
          <EmptyTitle>Acceso restringido</EmptyTitle>
          <EmptyDescription>
            No tienes los permisos necesarios para acceder a este módulo. Si crees que deberías
            tener acceso, contacta a tu administrador para que te asigne los permisos
            correspondientes.
          </EmptyDescription>
        </EmptyHeader>

        <EmptyContent>
          <Button variant="outline" className="w-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Volver atrás
          </Button>

          {accessibleModules.length > 0 && (
            <>
              <Separator />
              <p className="text-xs text-muted-foreground">Módulos a los que tienes acceso</p>
              <div className="grid w-full grid-cols-2 gap-2">
                {accessibleModules.map((m) => (
                  <Button
                    key={m.to}
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2"
                    asChild
                  >
                    <Link to={m.to}>
                      <m.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{m.label}</span>
                    </Link>
                  </Button>
                ))}
              </div>
            </>
          )}
        </EmptyContent>
      </Empty>
    </div>
  )
}

export default Unauthorized
